import { StrictMode, startTransition } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { StartClient } from "@tanstack/react-start/client";

/**
 * Overrides TanStack Start's default client entry (which unconditionally
 * calls hydrateRoot(document, ...)). dist/index.html is a single static
 * shell — only "/" is actually rendered through the SSR worker at build
 * time (see scripts/export-static.mjs) — and every other route reaches
 * that same file on a fresh page load via the .htaccess SPA fallback. Its
 * DOM never matches what the router expects to hydrate for those routes,
 * which threw a React #418 hydration-mismatch error before falling back to
 * a full re-render anyway. Skip straight to a plain client render whenever
 * the current path is not the one that was actually pre-rendered.
 */
const app = (
  <StrictMode>
    <StartClient />
  </StrictMode>
);

startTransition(() => {
  if (window.location.pathname === "/") {
    // scripts/export-static.mjs injects a #__route_boot script and
    // #__route_style stylesheet into <head> so a non-"/" route doesn't flash
    // the pre-rendered homepage before this file loads (see that script's
    // comment, and __root.tsx's RootComponent for the other half of this).
    // Neither is part of what SSR actually rendered for "/", so strip them —
    // along with the data-ssr-route attribute the boot script sets — before
    // hydrating, or React would hydrate against a <head> that doesn't match.
    // (Not needed on the non-"/" path below: that one fully re-renders
    // rather than hydrating, and RootComponent's layout effect — not this
    // file — is what reveals <body> there, since removing the stylesheet
    // here would race the transition's actual commit and flash stale
    // content in the gap.)
    document.getElementById("__route_boot")?.remove();
    document.getElementById("__route_style")?.remove();
    document.documentElement.removeAttribute("data-ssr-route");
    hydrateRoot(document, app);
  } else {
    createRoot(document).render(app);
  }
});
